import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { countryFromTimeZone, countryFromLanguage } from '@/utils/timezoneCountry';

const COUNTRY_KEY = 'bm_play_country';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const readSaved = () => {
  try { return localStorage.getItem(COUNTRY_KEY) || ''; } catch (e) { return ''; }
};

/**
 * Country + local price list, shared by the landing pages and the subscription page,
 * so a country chosen in one place is remembered in the other.
 *
 * `fetcher` lets the signed-in page use its authenticated client; without one we hit
 * the public endpoint, which is what the landing pages need.
 */
export function useCountryPrices({ enabled = true, fetcher } = {}) {
  const [countries, setCountries] = useState([]);
  const [country, setCountryState] = useState(readSaved);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = fetcher
      ? fetcher()
      : axios.get(`${BACKEND}/api/public/local-prices`).then(res => res.data);
    load.then(data => {
      if (cancelled) return;
      const list = data?.countries || [];
      setCountries(list);
      // Nothing saved: guess from IP, then the device time zone, then the language region.
      setCountryState(current => {
        if (current) return current;
        const known = (code) => (code && list.some(c => c.code === code) ? code : null);
        const guess = known(data?.detected_country)
          || known(countryFromTimeZone())
          || known(countryFromLanguage());
        if (guess) console.log('[BODYMAX_PLAY_COUNTRY] auto-detected', guess);
        return guess || '';
      });
      setLoading(false);
    }).catch(() => { if (!cancelled) { setCountries([]); setLoading(false); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const setCountry = useCallback((code) => {
    setCountryState(code);
    try { localStorage.setItem(COUNTRY_KEY, code); } catch (e) { /* private mode */ }
  }, []);

  const row = countries.find(c => c.code === country) || null;
  const eurRow = countries.find(c => c.currency === 'EUR') || null;

  const formatLocal = useCallback((amount, currency) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount}`;
    }
  }, []);

  /**
   * Roughly what a euro web price costs in the member's currency, using Google's own
   * country pricing as the exchange rate (local Play price ÷ euro Play price).
   * Null for euro countries — there is nothing to convert — and when we have no row.
   */
  const guideFor = useCallback((eurAmount, plan) => {
    if (!row || !eurRow || row.currency === 'EUR') return null;
    const local = Number(row[plan]);
    const euro = Number(eurRow[plan]);
    if (!local || !euro) return null;
    const converted = (eurAmount * local) / euro;
    const rounded = converted >= 50 ? Math.round(converted) : Math.round(converted * 100) / 100;
    return formatLocal(rounded, row.currency);
  }, [row, eurRow, formatLocal]);

  return { countries, country, setCountry, row, eurRow, loading, formatLocal, guideFor };
}

export default useCountryPrices;
